import { Button, Flex, Stack, Text } from '@chakra-ui/react'
import { faShieldCheck } from '@fortawesome/pro-duotone-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'
import { useCallback, useMemo, useState } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { MONTHS } from '@/utils/constants'
import { noOp } from '@/utils/helpers'
import normalizeBirthday from '@/utils/normalizeBirthday'

import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import { RPCReload } from '@/client/rpc'
import { BIRTH_YEARS } from '@/client/services/formInputs/onboarding'

import Form from '@/components/UI/Form/Form'
import type { FormInputType, FormValues } from '@/components/UI/Form/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'

const FormFooter = ({ disabled, submitting }: { disabled: boolean; submitting: boolean }) => (
  <Flex direction="row" mt={9} justifyContent="flex-end">
    <Button isDisabled={disabled} isLoading={submitting} type="submit">
      Continue
    </Button>
  </Flex>
)

export default function MissingDateOfBirthModal(): JSX.Element | null {
  const user = useUser()

  const [userSelection, setUserSelection] = useState<{
    dateOfBirth: string
    ageYears: number
  } | null>(null)

  const formInputs: FormInputType[] = useMemo(
    () => [
      {
        label: 'Month',
        type: 'select',
        name: 'month_of_birth',
        required: true,
        options: MONTHS.map((item, index) => ({ label: item, value: `${index + 1}` })),
        initialValue: null,
        size: 3,
      },
      {
        label: 'Year',
        type: 'select',
        name: 'year_of_birth',
        required: true,
        options: BIRTH_YEARS.map(item => ({ label: `${item}`, value: `${item}` })),
        initialValue: null,
        size: 3,
      },
    ],
    []
  )

  const handleUserSelection = useCallback((values: FormValues) => {
    const dateOfBirth = normalizeBirthday(
      DateTime.fromObject({
        year: Number.parseInt(`${values.year_of_birth}`, 10),
        month: Number.parseInt(`${values.month_of_birth}`, 10),
      })
    ).toISO()

    setUserSelection({
      dateOfBirth,
      ageYears: Math.abs(DateTime.fromISO(dateOfBirth).diffNow().as('years')) || 0,
    })
  }, [])

  const handleReset = useCallback(() => {
    setUserSelection(null)
  }, [])

  const [handleSubmit, isSubmitting] = useAPIWrapper(
    async () => {
      if (userSelection == null) {
        return
      }
      await RPCReload.setMyAge({ dateOfBirth: userSelection.dateOfBirth })
    },
    [userSelection],
    'Setting age'
  )

  return (
    <ModalV3
      open
      size="md"
      onClose={noOp}
      disableClose
      disableClickOutside
      body={() => (
        <Stack direction="column">
          <Flex direction="column" align="center" mb="16px">
            <FontAwesomeIcon
              size="3x"
              icon={faShieldCheck}
              color={chakraTokenToVar('fg.success')}
            />
          </Flex>
          <Text fontWeight="bold">Hi {user.firstName}! 👋</Text>

          {userSelection == null ? (
            <>
              <Text>
                We&apos;re updating Schoolhouse.world and need users who joined over a year ago to
                confirm their birth month and year. This information will remain private and helps
                us create a safer, more personalized experience for everyone.
              </Text>
              <Form
                name="MissingDateOfBirth"
                blocks={formInputs}
                onSubmit={handleUserSelection}
                FooterComponent={FormFooter}
              />
            </>
          ) : (
            <Flex direction="column">
              <Text>
                You have selected that you are approximately{' '}
                <b>{Math.floor(userSelection.ageYears)} years old</b>. Please make sure that this is
                correct before saving.
              </Text>
              <Flex direction="row" mt={9} justifyContent="flex-end">
                <Button variant="outline" onClick={handleReset} isDisabled={isSubmitting}>
                  Change
                </Button>
                <Button ml="16px" onClick={handleSubmit} isDisabled={isSubmitting}>
                  Save
                </Button>
              </Flex>
            </Flex>
          )}
        </Stack>
      )}
    />
  )
}
